import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Descriptions, Drawer, Spin, Table, Tag, Typography } from 'antd';

import { useAccess } from '../../../auth/AccessContext.jsx';
import FeeCell, { FeeRateCell } from '../../../components/FeeCell.jsx';
import { requestType } from '../../../domain/requestType.js';
import { formatMoney, formatUnits } from '../../../format';
import { PROVENANCE_LABEL, SOURCE_LABEL, TXN_TYPE_COLOUR, TXN_TYPE_LABEL, lotAge } from './register.js';

const { Text } = Typography;

const dash = (value, format = (v) => v) => (value === null || value === undefined || value === '' ? '—' : format(value));

const mappingColumns = [
    { title: 'Account (activation code)', dataIndex: 'accountActivationCode', key: 'account', render: (v) => <Text className="txn-id">{dash(v)}</Text> },
    { title: 'Strategy ref', dataIndex: 'strategyRef', key: 'strategyRef', render: (v) => <Text className="txn-id">{dash(v)}</Text> },
    { title: 'LIV account', dataIndex: 'livAccountId', key: 'livAccountId', render: (v) => dash(v) },
    { title: 'From', dataIndex: 'validFrom', key: 'validFrom', width: 110, render: (v) => dash(v) },
    { title: 'To', dataIndex: 'validTo', key: 'validTo', width: 110, render: (v) => (v ? v : <Tag color="green">Current</Tag>) },
    { title: 'Source', dataIndex: 'mappingSource', key: 'mappingSource', width: 110, render: (v) => dash(v) }
];

const balanceColumns = [
    { title: 'Fund', dataIndex: 'fundId', key: 'fundId', width: 100 },
    { title: 'Units', dataIndex: 'units', key: 'units', align: 'right', render: (v) => formatUnits(v) },
    { title: 'Cost (LKR)', dataIndex: 'costValue', key: 'costValue', align: 'right', render: (v) => dash(v, formatMoney) },
    { title: 'As of', dataIndex: 'asOfDate', key: 'asOfDate', width: 110, render: (v) => dash(v) }
];

const lotColumns = [
    { title: 'Lot', key: 'lot', width: 90, render: (_, l) => l.fundId + ' #' + l.lotNo },
    { title: 'Acquired', dataIndex: 'acquiredDate', key: 'acquiredDate', width: 110 },
    { title: 'Units left', key: 'units', align: 'right', render: (_, l) => <>{formatUnits(l.unitsRemaining)}<div><Text type="secondary" style={{ fontSize: 11.5 }}>of {formatUnits(l.unitsAcquired)}</Text></div></> },
    { title: 'Unit price', dataIndex: 'unitPrice', key: 'unitPrice', align: 'right', render: (v) => dash(v, formatUnits) },
    { title: 'Cost left (LKR)', dataIndex: 'costRemaining', key: 'costRemaining', align: 'right', render: (v) => dash(v, formatMoney) },
    { title: 'From', dataIndex: 'provenance', key: 'provenance', render: (v) => PROVENANCE_LABEL[v] ?? dash(v) },
    {
        title: 'Age',
        key: 'age',
        render: (_, l) => {
            if (l.status && l.status !== 'OPEN') return <Tag>{l.status}</Tag>;
            const age = lotAge(l);
            return age.young === null ? age.text : <Tag color={age.young ? 'gold' : 'green'}>{age.text}</Tag>;
        }
    }
];

const historyColumns = [
    { title: 'Effective date', dataIndex: 'dealingDate', key: 'dealingDate', width: 110 },
    { title: 'Fund', dataIndex: 'fundId', key: 'fundId', width: 80 },
    { title: 'Type', dataIndex: 'txnType', key: 'txnType', render: (v) => <Tag color={TXN_TYPE_COLOUR[v] ?? 'default'}>{TXN_TYPE_LABEL[v] ?? v}</Tag> },
    { title: 'Request type', key: 'requestType', render: (_, t) => dash(requestType(t)) },
    { title: 'Units', dataIndex: 'units', key: 'units', align: 'right', render: (v) => formatUnits(v) },
    { title: 'Price', dataIndex: 'price', key: 'price', align: 'right', render: (v) => dash(v, formatUnits) },
    // The fee and the % it was charged at, as the register's transaction history shows it.
    { title: 'Fee (LKR)', dataIndex: 'feeAmount', key: 'feeAmount', align: 'right', render: (_, t) => <FeeCell row={t} showRate={false} /> },
    { title: 'Fee %', key: 'feeRate', render: (_, t) => <FeeRateCell row={t} /> },
    { title: 'Balance after', dataIndex: 'balanceAfter', key: 'balanceAfter', align: 'right', render: (v) => dash(v, formatUnits) },
    { title: 'Source', dataIndex: 'source', key: 'source', render: (v) => SOURCE_LABEL[v] ?? dash(v) }
];

/**
 * One holder as GET register/holders/{id} answers it: who they are, their
 * LIV mappings (account and strategy codes), their balances and lots per
 * fund - each lot young until, or old since, the date the fund's exit-fee
 * holding months pass (IP-112; a dash when the fund has no fee configuration
 * in force) - and their latest transactions. Rendered by HolderDrawer, and on
 * its own by UnitRegistry.test.js.
 */
export const HolderDetail = ({ detail, onTransactions }) => {
    const { holder, mappings = [], balances = [], lots = [], history = [] } = detail;
    return (
        <div style={{ display: 'grid', gap: 16 }}>
            <Descriptions size="small" bordered column={2} items={[
                { key: 'no', label: 'Holder number', children: <Text className="txn-id">{holder.holderNo}</Text> },
                { key: 'type', label: 'Type', children: dash(holder.holderType) },
                { key: 'name', label: 'Name', children: holder.fullName, span: 2 },
                { key: 'status', label: 'Status', children: <>{dash(holder.status)}{holder.isSynthetic && <Tag style={{ marginLeft: 8 }}>Synthetic</Tag>}</> },
                { key: 'registered', label: 'Registered', children: dash(holder.registrationDate) }
            ]} />
            <Table rowKey={(m) => (m.livStrategyId ?? m.strategyRef ?? '') + ':' + m.validFrom} size="small" columns={mappingColumns} dataSource={mappings}
                pagination={false} title={() => 'LIV mappings'} locale={{ emptyText: 'No LIV mapping' }} />
            <Table rowKey="fundId" size="small" columns={balanceColumns} dataSource={balances} pagination={false} title={() => 'Balances'}
                locale={{ emptyText: 'No balance in any fund' }} />
            {detail.lotsTruncated && (
                <Alert type="info" showIcon className="auth-alert" message="Only the first lots are shown: this holder has more than one read carries." />
            )}
            <Table rowKey="lotId" size="small" columns={lotColumns} dataSource={lots} pagination={{ pageSize: 20, hideOnSinglePage: true }}
                title={() => 'Lots' + (detail.asOf ? ' (ages on ' + detail.asOf + ')' : '')} locale={{ emptyText: 'No lots' }} />
            <Table rowKey="txnId" size="small" columns={historyColumns} dataSource={history} pagination={{ pageSize: 20, hideOnSinglePage: true }}
                title={() => (
                    <span className="register-table-title">
                        Latest transactions
                        {onTransactions && <Button size="small" onClick={() => onTransactions(holder)}>All transactions</Button>}
                    </span>
                )}
                locale={{ emptyText: 'No transactions' }} />
        </div>
    );
};

/**
 * The holder's drawer: GET register/holders/{id}, read each time it opens. A
 * later read wins (CON-3): closing on holder A and opening holder B before A's
 * answer is back must never land A's mappings, balances and lots under B's
 * title, so an answer that is not the latest asked is dropped - the ticket
 * usePagedList and BankStatementTransactions keep.
 */
const HolderDrawer = ({ holder, onClose, onTransactions }) => {
    const { api } = useAccess();
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const latest = useRef(0);
    const holderId = holder?.holderId ?? null;

    const load = useCallback(async () => {
        const ticket = ++latest.current;
        if (!holderId) return;
        setLoading(true);
        setError(null);
        try {
            const answer = await api.register.holder(holderId);
            if (ticket !== latest.current) return;
            setDetail(answer);
        } catch (e) {
            if (ticket === latest.current) setError(e.message);
        } finally {
            if (ticket === latest.current) setLoading(false);
        }
    }, [api, holderId]);

    useEffect(() => {
        setDetail(null);
        load();
    }, [load]);

    return (
        <Drawer open={Boolean(holderId)} onClose={onClose} width={900} rootClassName="lfoc-styles-wrapper"
            title={holder ? holder.fullName + ' (' + holder.holderNo + ')' : 'Holder'}>
            {error && <Alert type="error" showIcon message={error} className="auth-alert" />}
            {loading && !detail && <Spin />}
            {detail?.holder && <HolderDetail detail={detail} onTransactions={onTransactions} />}
        </Drawer>
    );
};

export default HolderDrawer;
